import { BackIcon, ForwardIcon, LoadingIcon, SearchIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { SearchAndFilter, type DeckardFilter, type SearchDetailTypes } from '@components/filter';
import { Checkbox } from '@components/form';
import { Group } from '@components/layout';
import { ServerTag, type ServerTagProps } from '@components/server';
import { Typography } from '@components/typography';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

type TransferListProps<T extends { name: string }, F> = {
  availableLoading: boolean;
  availableItems: T[];
  selectedItems: T[];
  selectedLoading: boolean;
  resourceTitle: string;
  resourceIdKey: keyof T;
  editable?: boolean;
  selectedTitle?: string;
  availableTitle?: string;
  emptySelectedText?: string;
  emptyAvailableText?: string;
  label?: (item: T) => string;
  tag?: (item: T) => string | undefined;
  searchAndFilterProps?: {
    filter: DeckardFilter<F>;
    setFilter: (filter: DeckardFilter<F>) => void;
    searchDetail: SearchDetailTypes;
    context: string;
  };
  onAdd: (items: T[]) => Promise<void>;
  onRemove: (items: T[]) => Promise<void>;
  onViewDetails?: (item: T) => void;
  onFetchMoreAvailable?: VoidFunction;
};

export const TransferList = <T extends { name: string }, F>({
  availableItems,
  availableLoading,
  selectedItems,
  selectedLoading,
  resourceTitle,
  resourceIdKey,
  editable = true,
  selectedTitle,
  availableTitle,
  emptySelectedText,
  emptyAvailableText,
  label,
  tag,
  searchAndFilterProps,
  onAdd,
  onRemove,
  onFetchMoreAvailable,
  onViewDetails,
}: TransferListProps<T, F>) => {
  const { palette, breakpoints } = useTheme();
  const isVertical = useMediaQuery(breakpoints.down('md'));

  const [selectedCurrent, setSelectedCurrent] = useState<Set<string>>(new Set());
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set());
  const [transferring, setTransferring] = useState(false);

  const currentItemIds = useMemo(
    () => new Set(selectedItems.map((item) => item[resourceIdKey] as string)),
    [selectedItems, resourceIdKey],
  );

  const filteredAvailable = useMemo(
    () =>
      availableItems
        .filter((item) => !currentItemIds.has(item[resourceIdKey] as string))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [availableItems, currentItemIds, resourceIdKey],
  );

  const toggleCurrent = useCallback((id: string) => {
    setSelectedCurrent((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAvailable = useCallback((id: string) => {
    setSelectedAvailable((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAddButtonClick = async () => {
    if (selectedAvailable.size === 0) return;
    setTransferring(true);
    try {
      const selectedItemsToAdd = filteredAvailable.filter((item) =>
        selectedAvailable.has(item[resourceIdKey] as string),
      );
      await onAdd(selectedItemsToAdd);
      setSelectedAvailable(new Set());
    } finally {
      setTransferring(false);
    }
  };

  const handleRemoveButtonClick = async () => {
    if (selectedCurrent.size === 0) return;
    setTransferring(true);
    try {
      const selectedItemsToRemove = selectedItems.filter((item) =>
        selectedCurrent.has(item[resourceIdKey] as string),
      );
      await onRemove(selectedItemsToRemove);
      setSelectedCurrent(new Set());
    } finally {
      setTransferring(false);
    }
  };

  const handleAvailableScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        onFetchMoreAvailable?.();
      }
    },
    [onFetchMoreAvailable],
  );

  return (
    <Stack
      direction={isVertical ? 'column' : 'row'}
      gap={2}
      sx={{
        height: '100%',
        minHeight: isVertical ? '400px' : '300px',
        maxHeight: isVertical ? '70vh' : '600px',
      }}
    >
      {/* Selected items */}
      <Stack flex={1} sx={{ overflow: 'hidden', minWidth: 0 }} gap={2}>
        <Typography variant="body2" color="foreground.paper.light">
          {selectedTitle || `Attached ${resourceTitle} (${selectedItems.length})`}
        </Typography>
        <Stack
          sx={{
            flex: 1,
            overflow: 'auto',
            py: 1,
            backgroundColor: palette.background.paperCard,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {selectedLoading ? (
            <Stack flex={1} alignItems="center" justifyContent="center" p={4}>
              <LoadingIcon size={16} />
            </Stack>
          ) : selectedItems.length === 0 ? (
            <Stack flex={1} alignItems="center" justifyContent="center" p={4}>
              <Typography variant="body2" color="foreground.paperCard.light">
                {emptySelectedText || `No ${resourceTitle} attached`}
              </Typography>
            </Stack>
          ) : (
            [...selectedItems]
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              .map((item) => (
                <ResourceListItem<T>
                  key={item[resourceIdKey] as string}
                  resource={item}
                  label={label?.(item)}
                  idKey={resourceIdKey}
                  tag={tag?.(item)}
                  selected={selectedCurrent.has(item[resourceIdKey] as string)}
                  editable={editable}
                  onToggle={toggleCurrent}
                  onViewDetails={onViewDetails}
                />
              ))
          )}
        </Stack>
      </Stack>

      {/* Transfer Buttons */}
      <ResourceTransferListButtons
        onAdd={handleAddButtonClick}
        onRemove={handleRemoveButtonClick}
        isVertical={isVertical}
        addDisabled={!editable || selectedAvailable.size === 0 || transferring}
        removeDisabled={!editable || selectedCurrent.size === 0 || transferring}
      />

      {/* Available items */}
      <Stack flex={1} sx={{ overflow: 'hidden', minWidth: 0 }}>
        <Stack gap={2} mb={searchAndFilterProps ? 0 : 2}>
          <Typography variant="body2" color="foreground.paper.light">
            {availableTitle || `Available ${resourceTitle}`}
          </Typography>
          {searchAndFilterProps && (
            <SearchAndFilter {...searchAndFilterProps} variant="secondary" />
          )}
        </Stack>
        <Stack
          sx={{
            flex: 1,
            overflow: 'auto',
            py: 1,
            backgroundColor: palette.background.paperCard,
            border: '1px solid',
            borderColor: 'divider',
          }}
          onScroll={handleAvailableScroll}
        >
          {availableLoading ? (
            <Stack flex={1} alignItems="center" justifyContent="center" p={4}>
              <LoadingIcon size={16} />
            </Stack>
          ) : filteredAvailable.length === 0 ? (
            <Stack flex={1} alignItems="center" justifyContent="center" p={4}>
              <Typography variant="body2" color="foreground.paperCard.light">
                {emptyAvailableText || `No available ${resourceTitle} found`}
              </Typography>
            </Stack>
          ) : (
            filteredAvailable.map((item) => (
              <ResourceListItem<T>
                key={item[resourceIdKey] as string}
                resource={item}
                label={label?.(item)}
                idKey={resourceIdKey}
                tag={tag?.(item)}
                selected={selectedAvailable.has(item[resourceIdKey] as string)}
                editable={editable}
                onToggle={toggleAvailable}
                onViewDetails={onViewDetails}
              />
            ))
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

const ResourceTransferListButtons = ({
  onAdd,
  onRemove,
  isVertical,
  addDisabled,
  removeDisabled,
}: {
  onAdd: () => void;
  onRemove: () => void;
  isVertical: boolean;
  addDisabled: boolean;
  removeDisabled: boolean;
}) => {
  const { palette } = useTheme();
  return (
    <Stack
      direction={isVertical ? 'row-reverse' : 'column'}
      alignItems="center"
      justifyContent="center"
      gap={1}
    >
      <IconButton
        Icon={ForwardIcon}
        tooltip="Remove selected items"
        disabled={removeDisabled}
        onClick={onRemove}
        size={24}
        color={palette.foreground.paper.default}
        sx={{ transform: isVertical ? 'rotate(90deg)' : 'none' }}
      />
      <IconButton
        Icon={BackIcon}
        tooltip="Add selected items"
        disabled={addDisabled}
        onClick={onAdd}
        size={24}
        color={palette.foreground.paper.default}
        sx={{ transform: isVertical ? 'rotate(90deg)' : 'none' }}
      />
    </Stack>
  );
};

type ResourceListItemProps<T> = {
  label?: string;
  resource: Partial<T>;
  tag?: string;
  tagProps?: ServerTagProps;
  idKey: keyof T;
  selected: boolean;
  editable?: boolean;
  onToggle: (id: string) => void;
  onViewDetails?: (resource: Partial<T>) => void;
};

const ResourceListItem = <T extends { name?: string }>({
  label,
  resource,
  tag,
  idKey,
  tagProps,
  selected,
  editable = true,
  onToggle,
  onViewDetails,
}: ResourceListItemProps<T>) => {
  return (
    <Group
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      sx={{
        px: 3,
        py: 2,
        cursor: editable ? 'pointer' : 'default',
        '&:hover': editable ? { backgroundColor: 'action.hover' } : {},
        borderRadius: 1,
        opacity: editable ? 1 : 0.7,
      }}
      onClick={editable ? () => onToggle(resource[idKey] as string) : undefined}
    >
      <Group alignItems="center" gap={3}>
        <Checkbox checked={selected} disabled={!editable} sx={{ mr: 0, height: 'min-content' }} />
        <Typography variant="body2" handleOverFlow sx={{ flex: 1, minWidth: 0 }}>
          {label || resource.name}
        </Typography>
      </Group>
      <Group alignItems="center" justifyContent="flex-end" flex={1} flexShrink={0} gap={1}>
        {tag && <ServerTag color="default" variant="caption2" label={tag} {...tagProps} />}
        {onViewDetails && (
          <IconButton
            Icon={SearchIcon}
            tooltip="Details"
            onClick={(event) => {
              event.stopPropagation();
              onViewDetails(resource);
            }}
            size={16}
            variant="default"
          />
        )}
      </Group>
    </Group>
  );
};
